import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getStroke } from 'perfect-freehand';
import { fontCss, fontDef, ensureFontsLoaded } from './whiteboardFonts';

const PEN_OPTIONS = {
  thinning: 0,
  smoothing: 0.5,
  streamline: 0.5,
  simulatePressure: false,
  start: { cap: true, taper: 0 },
  end: { cap: true, taper: 0 },
};

// Catmull-Rom spline: fills sparse samples from fast pointer movement so curves stay round.
const interpolate = (pts, maxGap = 6) => {
  if (pts.length < 3) return pts;
  const out = [pts[0]];
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[Math.max(0, i - 1)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(pts.length - 1, i + 2)];
    const gap = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
    const n = Math.min(24, Math.ceil(gap / maxGap));
    for (let k = 1; k <= n; k += 1) {
      const t = k / n, t2 = t * t, t3 = t2 * t;
      out.push([
        0.5 * ((2 * p1[0]) + (-p0[0] + p2[0]) * t + (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 + (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3),
        0.5 * ((2 * p1[1]) + (-p0[1] + p2[1]) * t + (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 + (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3),
        p2[2],
      ]);
    }
  }
  return out;
};

const outlinePath = (points, size, last) => {
  const outline = getStroke(interpolate(points), { ...PEN_OPTIONS, size, last });
  const path = new Path2D();
  if (!outline.length) return path;
  path.moveTo(outline[0][0], outline[0][1]);
  for (let i = 1; i < outline.length; i += 1) path.lineTo(outline[i][0], outline[i][1]);
  path.closePath();
  return path;
};

export const drawPen = (ctx, item, last = true) => {
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = item.color;
  ctx.fill(outlinePath(item.points, item.width, last));
};

export const drawEraseSegment = (ctx, width, from, to) => {
  ctx.globalCompositeOperation = 'destination-out';
  ctx.strokeStyle = 'rgba(0,0,0,1)';
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(from[0], from[1]);
  ctx.lineTo(to[0] + (from === to ? 0.01 : 0), to[1]);
  ctx.stroke();
};

const FONT = (size, font) => fontCss(size, font);
const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
const measureCtx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;

// WB-TEXT-EDIT-001: bounding box of a text item (per-line measureText), used only by the Text tool.
export const textBox = (item) => {
  const lines = item.text.split('\n');
  let w = 0;
  if (measureCtx) { measureCtx.font = FONT(item.size, item.font); lines.forEach((l) => { w = Math.max(w, measureCtx.measureText(l || ' ').width); }); }
  return { x: item.x - 4, y: item.y - 3, w: Math.max(w, 12) + 8, h: lines.length * item.size * 1.25 + 6 };
};
const inBox = (b, x, y) => x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;

const drawItem = (ctx, item) => {
  if (item.type === 'text') {
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = item.color;
    ctx.font = FONT(item.size, item.font);
    ctx.textBaseline = 'top';
    item.text.split('\n').forEach((line, i) => ctx.fillText(line, item.x, item.y + i * item.size * 1.25));
    return;
  }
  if (item.type === 'stroke') { drawPen(ctx, item); return; }
  const pts = item.points;
  for (let i = 0; i < pts.length; i += 1) drawEraseSegment(ctx, item.width, pts[Math.max(0, i - 1)], pts[i]);
};

const setupCanvas = (canvas, w, h) => {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
  return ctx;
};

export const WhiteboardCanvas = ({ items, tool, color, width, eraserSize, textSize, textFont, showGrid, onCommit, onUpdate, onRemove, onSelectText, onStrokeStart, inkBox, flashTextId, renderOverlay }) => {
  const canvasRef = useRef(null);
  const liveCanvasRef = useRef(null);
  const wrapRef = useRef(null);
  const liveRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [textDraft, setTextDraft] = useState(null);
  const [hover, setHover] = useState(null);
  // WB-TEXT-EDIT-001 — text-object layer (Text tool only)
  const [selectedId, setSelectedId] = useState(null);
  const [hoverTextId, setHoverTextId] = useState(null);
  const [drag, setDrag] = useState(null); // { id, dx, dy }
  const dragRef = useRef(null);
  const skipCommitRef = useRef(false);
  const textItems = items.map((it, index) => ({ it, index, key: it.id ?? `idx-${index}` })).filter(({ it }) => it.type === 'text');
  const findText = (key) => textItems.find((t) => t.key === key) || null;
  const hitText = (x, y) => [...textItems].reverse().find(({ it }) => inBox(textBox(it), x, y)) || null;
  const selected = selectedId ? findText(selectedId) : null;

  useEffect(() => { if (tool !== 'text') { setSelectedId(null); setHoverTextId(null); } }, [tool]);

  // Colour / size dots apply to the selected text (not while typing — the draft handles that)
  const latest = useRef({}); latest.current = { selected, textDraft, onUpdate };
  useEffect(() => {
    const { selected: sel, textDraft: d, onUpdate: upd } = latest.current;
    if (d) { setTextDraft((x) => (x ? { ...x, color } : x)); return; }
    if (sel && sel.it.color !== color) upd(sel.index, { ...sel.it, id: sel.it.id ?? sel.key, color });
  }, [color]);  
  useEffect(() => {
    const { selected: sel, textDraft: d, onUpdate: upd } = latest.current;
    if (d) { setTextDraft((x) => (x ? { ...x, size: textSize } : x)); return; }
    if (sel && sel.it.size !== textSize) upd(sel.index, { ...sel.it, id: sel.it.id ?? sel.key, size: textSize });
  }, [textSize]);  
  // WB-TEXT-FMT-001: font style applies to the selected text or the live draft the same way
  useEffect(() => {
    const { selected: sel, textDraft: d, onUpdate: upd } = latest.current;
    if (d) { setTextDraft((x) => (x ? { ...x, font: textFont } : x)); return; }
    if (sel && (sel.it.font || 'clean') !== textFont) upd(sel.index, { ...sel.it, id: sel.it.id ?? sel.key, font: textFont });
  }, [textFont]);  
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => { let alive = true; ensureFontsLoaded().then(() => alive && setFontsReady(true)); return () => { alive = false; }; }, []);

  // Delete / Backspace removes the selected text (never while a textarea/input has focus)
  useEffect(() => {
    if (!selected || textDraft || tool !== 'text') return undefined;
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'textarea' || tag === 'input') return;
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); onRemove(selected.index); setSelectedId(null); }
      if (e.key === 'Escape') setSelectedId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, textDraft, tool, onRemove]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const redraw = useCallback(() => {
    if (!canvasRef.current || !size.w) return;
    const ctx = setupCanvas(canvasRef.current, size.w, size.h);
    items.forEach((it, index) => {
      const key = it.id ?? `idx-${index}`;
      if (it.type === 'text' && textDraft?.id === key) return; // being edited — textarea shows it
      if (it.type === 'text' && drag?.id === key) { drawItem(ctx, { ...it, x: it.x + drag.dx, y: it.y + drag.dy }); return; }
      drawItem(ctx, it);
    });
    ctx.globalCompositeOperation = 'source-over';
    setupCanvas(liveCanvasRef.current, size.w, size.h);
  }, [items, size, textDraft?.id, drag, fontsReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { redraw(); }, [redraw]);

  const getPoints = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const native = e.nativeEvent || e;
    const events = native.getCoalescedEvents ? native.getCoalescedEvents() : [];
    const list = events.length ? events : [native];
    return list.map((ev) => [ev.clientX - rect.left, ev.clientY - rect.top, ev.pressure || 0.5]);
  };

  const renderLive = () => {
    const live = liveRef.current;
    if (!live) return;
    if (live.type === 'stroke') {
      const ctx = setupCanvas(liveCanvasRef.current, size.w, size.h);
      drawPen(ctx, live, false);
    } else {
      const ctx = canvasRef.current.getContext('2d');
      const pts = live.points;
      drawEraseSegment(ctx, live.width, pts[Math.max(0, pts.length - 2)], pts[pts.length - 1]);
      ctx.globalCompositeOperation = 'source-over';
    }
  };

  const commitText = useCallback(() => {
    if (skipCommitRef.current) { skipCommitRef.current = false; setTextDraft(null); return; }
    if (!textDraft) return;
    const value = textDraft.value;
    if (textDraft.id) {
      const t = findText(textDraft.id);
      if (t) {
        if (!value.trim()) onRemove(t.index);
        else if (value !== t.it.text || textDraft.color !== t.it.color || textDraft.size !== t.it.size || (t.it.font || 'clean') !== textDraft.font) {
          onUpdate(t.index, { ...t.it, id: t.it.id ?? t.key, text: value, color: textDraft.color, size: textDraft.size, font: textDraft.font });
        }
      }
      setSelectedId(value.trim() ? textDraft.id : null);
    } else if (value.trim()) {
      onCommit({ type: 'text', id: newId(), x: textDraft.x, y: textDraft.y, text: value, color: textDraft.color, size: textDraft.size, font: textDraft.font });
    }
    setTextDraft(null);
  }, [textDraft, onCommit, onUpdate, onRemove]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelText = () => { skipCommitRef.current = true; setTextDraft(null); };
  const startEdit = (t) => {
    setSelectedId(t.key);
    setTextDraft({ id: t.key, x: t.it.x, y: t.it.y, value: t.it.text, color: t.it.color, size: t.it.size, font: t.it.font || 'clean' });
  };

  const onPointerDown = (e) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    const [p] = getPoints(e);
    if (tool === 'text') {
      e.preventDefault();
      if (textDraft) { commitText(); return; }
      const hit = hitText(p[0], p[1]);
      if (hit) {
        setSelectedId(hit.key);
        onSelectText?.(hit.it);
        dragRef.current = { id: hit.key, startX: p[0], startY: p[1], moved: false };
        liveCanvasRef.current.setPointerCapture(e.pointerId);
        return;
      }
      setSelectedId(null);
      setTextDraft({ x: p[0], y: p[1], value: '', color, size: textSize, font: textFont });
      return;
    }
    liveCanvasRef.current.setPointerCapture(e.pointerId);
    if (tool === 'pen') onStrokeStart?.();
    liveRef.current = {
      type: tool === 'eraser' ? 'erase' : 'stroke',
      id: newId(),
      color,
      width: tool === 'eraser' ? eraserSize : width,
      points: [p],
    };
    renderLive();
  };

  const onPointerMove = (e) => {
    if (tool === 'text') {
      const [p] = getPoints(e);
      const d = dragRef.current;
      if (d) {
        const dx = p[0] - d.startX, dy = p[1] - d.startY;
        if (d.moved || Math.abs(dx) + Math.abs(dy) > 2) { d.moved = true; setDrag({ id: d.id, dx, dy }); }
        return;
      }
      if (!textDraft) setHoverTextId(hitText(p[0], p[1])?.key || null);
      return;
    }
    if (tool === 'eraser') {
      const rect = canvasRef.current.getBoundingClientRect();
      setHover({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
    const live = liveRef.current;
    if (!live) return;
    const pts = getPoints(e);
    const lastPt = live.points[live.points.length - 1];
    pts.forEach((pt) => {
      const prev = live.points[live.points.length - 1] || lastPt;
      if (Math.abs(pt[0] - prev[0]) + Math.abs(pt[1] - prev[1]) >= 0.5) live.points.push(pt);
    });
    renderLive();
  };

  const onPointerUp = () => {
    const d = dragRef.current;
    if (d) {
      dragRef.current = null;
      const t = findText(d.id);
      if (d.moved && t && drag) onUpdate(t.index, { ...t.it, id: t.it.id ?? t.key, x: t.it.x + drag.dx, y: t.it.y + drag.dy });
      setDrag(null);
      return;
    }
    if (!liveRef.current) return;
    const done = liveRef.current;
    liveRef.current = null;
    onCommit(done);
  };

  const cursor = tool === 'text' ? (drag ? 'move' : hoverTextId ? 'pointer' : 'text') : tool === 'eraser' ? 'none' : 'crosshair';
  const onDoubleClick = (e) => {
    if (tool !== 'text' || textDraft) return;
    const [p] = getPoints(e);
    const hit = hitText(p[0], p[1]);
    if (hit) { e.preventDefault(); startEdit(hit); }
  };
  const selBox = selected && !textDraft ? textBox(drag?.id === selected.key ? { ...selected.it, x: selected.it.x + drag.dx, y: selected.it.y + drag.dy } : selected.it) : null;
  const flashItem = flashTextId ? items.find((it) => it.type === 'text' && it.id === flashTextId) : null;
  const flashBox = flashItem ? textBox(flashItem) : null;

  return (
    <div
      ref={wrapRef}
      data-testid="whiteboard-canvas-wrap"
      className={`relative flex-1 min-h-0 bg-white overflow-hidden select-none ${showGrid ? 'wb-dot-grid' : ''}`}
    >
      <canvas
        ref={canvasRef}
        data-testid="whiteboard-canvas"
        data-tool={tool}
        data-item-count={items.length}
        style={{ width: size.w, height: size.h, display: 'block' }}
      />
      <canvas
        ref={liveCanvasRef}
        data-testid="whiteboard-live-canvas"
        className="absolute inset-0"
        style={{ width: size.w, height: size.h, cursor, touchAction: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={(e) => { setHover(null); setHoverTextId(null); onPointerUp(e); }}
        onDoubleClick={onDoubleClick}
      />
      {selBox && (
        <div
          data-testid="whiteboard-text-selection"
          className="absolute pointer-events-none rounded-sm border border-dashed border-blue-500/80 bg-blue-500/[.04] wb-sel"
          style={{ left: selBox.x, top: selBox.y, width: selBox.w, height: selBox.h }}
        />
      )}
      {inkBox && (
        <div
          data-testid="whiteboard-ink-group"
          className="absolute pointer-events-none rounded-md border border-dashed border-amber-400/80 bg-amber-300/[.06] wb-sel"
          style={{ left: inkBox.x - 6, top: inkBox.y - 6, width: inkBox.w + 12, height: inkBox.h + 12 }}
        />
      )}
      {flashBox && (
        <div
          data-testid="whiteboard-ink-flash"
          className="absolute pointer-events-none rounded-sm wb-ink-flash"
          style={{ left: flashBox.x - 2, top: flashBox.y - 2, width: flashBox.w + 4, height: flashBox.h + 4 }}
        />
      )}
      {renderOverlay?.(size)}
      {tool === 'eraser' && hover && (
        <div
          data-testid="whiteboard-eraser-cursor"
          className="absolute pointer-events-none rounded-full border-2 border-slate-500/80 bg-slate-400/15 shadow-[0_0_0_1px_rgba(255,255,255,0.9)]"
          style={{ left: hover.x - eraserSize / 2, top: hover.y - eraserSize / 2, width: eraserSize, height: eraserSize }}
        />
      )}
      {textDraft && (
        <textarea
          ref={(el) => el && setTimeout(() => el.focus(), 0)}
          data-testid="whiteboard-text-input"
          value={textDraft.value}
          placeholder="Type, Enter to save · Esc to cancel"
          data-editing={textDraft.id ? 'existing' : 'new'}
          onChange={(e) => setTextDraft((d) => ({ ...d, value: e.target.value }))}
          onBlur={commitText}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); cancelText(); }
            else if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitText(); }
          }}
          rows={1}
          className="absolute bg-transparent outline-none border border-dashed border-blue-400 rounded-sm px-0.5 resize-none overflow-hidden z-10"
          style={{
            left: textDraft.x - 2,
            top: textDraft.y - 2,
            color: textDraft.color,
            fontSize: Math.round(textDraft.size * fontDef(textDraft.font).scale),
            lineHeight: `${textDraft.size * 1.25}px`,
            fontFamily: fontDef(textDraft.font).family,
            fontWeight: fontDef(textDraft.font).weight,
            minWidth: 160,
            maxWidth: Math.max(160, size.w - textDraft.x - 8),
          }}
          onInput={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = `${e.target.scrollHeight}px`;
          }}
        />
      )}
    </div>
  );
};
